import {
  type CSSProperties,
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  adjustLabelTimingsForIntro,
  buildFrameLabelCSS,
  buildFrameLabelTimings,
} from "../core/labels";
import {
  normalizeFrames,
  extractValue,
  extractItemConfig,
} from "../core/normalize";
import { calculateTotalDuration } from "../core/durations";
import {
  applySequentialIntro,
  applySequentialTransitions,
  TimelineEntry,
} from "../core/timelines";
import { isLightColor } from "../core/color";
import {
  FRAME_LABEL_VERTICAL_SPACE,
  defaultHold,
  defaultOptions,
} from "../core/constants";
import { Frame } from "../types";
import { useAnimator, AnimatorHandle } from "../hooks/useAnimator";

let idCounter = 0;

const EASING_PRESETS: Record<string, [number, number, number, number]> = {
  ease: [0.25, 0.1, 0.25, 1],
  "ease-in": [0.42, 0, 1, 1],
  "ease-out": [0, 0, 0.58, 1],
  "ease-in-out": [0.42, 0, 0.58, 1],
};

const createCubicBezier = (
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
) => {
  if (p1x === p1y && p2x === p2y) {
    return (t: number) => t;
  }

  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;

  const sampleCurveX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleCurveY = (t: number) => ((ay * t + by) * t + cy) * t;
  const sampleCurveDerivativeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;

  const solveCurveX = (x: number) => {
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const x2 = sampleCurveX(t) - x;
      if (Math.abs(x2) < 1e-6) return t;
      const d2 = sampleCurveDerivativeX(t);
      if (Math.abs(d2) < 1e-6) break;
      t -= x2 / d2;
    }

    let t0 = 0;
    let t1 = 1;
    t = x;
    while (t0 < t1) {
      const x2 = sampleCurveX(t);
      if (Math.abs(x2 - x) < 1e-6) return t;
      if (x > x2) {
        t0 = t;
      } else {
        t1 = t;
      }
      t = (t0 + t1) / 2;
    }
    return t;
  };

  return (x: number) => sampleCurveY(solveCurveX(x));
};

const countDecimals = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  const text = Math.abs(value).toString();
  if (!text.includes("e")) {
    const [, fraction = ""] = text.split(".");
    return fraction.length;
  }

  const [coeff, exp] = text.split("e");
  const exponent = Number.parseInt(exp, 10);
  const fractionLength = (coeff.split(".")[1] || "").length;
  if (Number.isNaN(exponent)) return fractionLength;
  return Math.max(0, fractionLength - exponent);
};

const ABBREVIATIONS = [
  { value: 1e12, suffix: "T" },
  { value: 1e9, suffix: "B" },
  { value: 1e6, suffix: "M" },
  { value: 1e3, suffix: "k" },
];

const roundToSignificant = (value: number, digits: number) => {
  if (!Number.isFinite(value)) return 0;
  if (value === 0) return 0;
  const abs = Math.abs(value);
  const scale = 10 ** (digits - 1 - Math.floor(Math.log10(abs)));
  return Math.round(value * scale) / scale;
};

const resolveEasing = (easing: string) => {
  const normalized = easing?.trim() || "linear";
  if (normalized === "linear") {
    return (t: number) => t;
  }

  const preset = EASING_PRESETS[normalized];
  if (preset) {
    return createCubicBezier(...preset);
  }

  const match = normalized.match(/cubic-bezier\(([^)]+)\)/i);
  if (match) {
    const parts = match[1]
      .split(",")
      .map((value) => Number.parseFloat(value.trim()));
    if (parts.length === 4 && parts.every((value) => Number.isFinite(value))) {
      return createCubicBezier(parts[0], parts[1], parts[2], parts[3]);
    }
  }

  return (t: number) => t;
};

const buildValueTimeline = (
  values: number[],
  holdStart: number,
  holdEnd: number,
  duration: number,
  advanceDelay: number,
): TimelineEntry[] => {
  const frameCount = values.length;
  if (frameCount === 0) return [];

  const safeValues = values.map((value) =>
    Number.isFinite(value) ? value : 0,
  );
  const entries: TimelineEntry[] = [];
  const startHold = Math.max(0, Math.min(holdStart, 100));
  const endHold = Math.max(0, Math.min(holdEnd, 100 - startHold));
  const startHoldMs = (startHold / 100) * duration;
  const endHoldMs = (endHold / 100) * duration;
  const animatedDuration = Math.max(0, duration - startHoldMs - endHoldMs);
  const stepTime = frameCount > 1 ? animatedDuration / (frameCount - 1) : 0;
  const pause = Math.max(0, advanceDelay);
  const totalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frameCount,
  );
  const toPercent = (timeMs: number) => (timeMs / totalDuration) * 100;

  const firstValue = safeValues[0];
  entries.push({ percent: 0, height: firstValue });
  if (startHoldMs > 0) {
    entries.push({ percent: toPercent(startHoldMs), height: firstValue });
  }

  let currentTime = startHoldMs;
  for (let index = 1; index < frameCount; index += 1) {
    const prevValue = safeValues[index - 1];
    if (pause > 0) {
      currentTime += pause;
      entries.push({ percent: toPercent(currentTime), height: prevValue });
    }
    currentTime += stepTime;
    entries.push({
      percent: toPercent(currentTime),
      height: safeValues[index],
    });
  }

  const lastValue = safeValues[frameCount - 1];
  if (endHoldMs > 0) {
    const endHoldStart = totalDuration - endHoldMs;
    entries.push({ percent: toPercent(endHoldStart), height: lastValue });
    entries.push({ percent: 100, height: lastValue });
  }

  if (!entries.some((entry) => entry.percent === 0)) {
    entries.unshift({ percent: 0, height: firstValue });
  }
  if (!entries.some((entry) => entry.percent === 100)) {
    entries.push({ percent: 100, height: lastValue });
  }

  return entries.filter(
    (entry, index, arr) =>
      index === 0 || entry.percent !== arr[index - 1].percent,
  );
};

const interpolateTimeline = (
  timeline: TimelineEntry[],
  percent: number,
  easingFn: (t: number) => number,
) => {
  if (!timeline.length) return 0;

  const clamped = Math.max(0, Math.min(100, percent));
  if (clamped <= timeline[0].percent) return timeline[0].height;

  for (let i = 1; i < timeline.length; i += 1) {
    const prev = timeline[i - 1];
    const next = timeline[i];
    if (clamped <= next.percent) {
      const span = next.percent - prev.percent;
      if (span <= 0) return next.height;
      const local = (clamped - prev.percent) / span;
      const eased = easingFn(local);
      return prev.height + (next.height - prev.height) * eased;
    }
  }

  return timeline[timeline.length - 1].height;
};

const getValueAtPercent = (timeline: TimelineEntry[], percent: number) => {
  if (!timeline.length) return 0;
  const clamped = Math.max(0, Math.min(100, percent));
  if (clamped <= timeline[0].percent) return timeline[0].height;
  for (let i = 1; i < timeline.length; i += 1) {
    const prev = timeline[i - 1];
    const next = timeline[i];
    if (clamped <= next.percent) {
      const span = next.percent - prev.percent;
      if (span <= 0) return next.height;
      const local = (clamped - prev.percent) / span;
      return prev.height + (next.height - prev.height) * local;
    }
  }
  return timeline[timeline.length - 1].height;
};

const getIterationProgress = (
  elapsedMs: number,
  delayMs: number,
  iterationDuration: number,
  iterations: number | "infinite",
  loopMode: "normal" | "alternate",
) => {
  if (elapsedMs < delayMs) return 0;
  const localElapsed = elapsedMs - delayMs;
  if (iterationDuration <= 0) return 1;

  const totalIterations =
    typeof iterations === "number" ? Math.max(0, iterations) : Infinity;
  const totalMs = iterationDuration * totalIterations;
  if (Number.isFinite(totalMs) && localElapsed >= totalMs) {
    const finalIteration = Math.max(0, totalIterations - 1);
    const isReverse = loopMode === "alternate" && finalIteration % 2 === 1;
    return isReverse ? 0 : 1;
  }

  const iterationIndex = Math.floor(localElapsed / iterationDuration);
  let progress = (localElapsed % iterationDuration) / iterationDuration;
  if (loopMode === "alternate" && iterationIndex % 2 === 1) {
    progress = 1 - progress;
  }
  return progress;
};

const formatValueLabel = (value: number, finalValue: number) => {
  if (!Number.isFinite(value)) return "--";

  const cappedDecimals = countDecimals(finalValue);
  const absValue = Math.abs(value);
  let suffix = "";
  let scaled = value;

  for (const abbrev of ABBREVIATIONS) {
    if (absValue >= abbrev.value) {
      suffix = abbrev.suffix;
      scaled = value / abbrev.value;
      break;
    }
  }

  let rounded = roundToSignificant(scaled, 3);
  const absRounded = Math.abs(rounded);

  if (absRounded >= 1000 && suffix) {
    const currentIndex = ABBREVIATIONS.findIndex(
      (item) => item.suffix === suffix,
    );
    if (currentIndex > 0) {
      const next = ABBREVIATIONS[currentIndex - 1];
      suffix = next.suffix;
      rounded = roundToSignificant(value / next.value, 3);
    }
  }

  const digits = absRounded >= 1 ? Math.floor(Math.log10(absRounded)) + 1 : 0;
  const requiredDecimals = absRounded >= 1 ? Math.max(0, 3 - digits) : 3;
  const decimals = Math.min(requiredDecimals, cappedDecimals);
  const formatted = Math.abs(rounded).toFixed(decimals);
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${formatted}${suffix}`;
};

const polarToCartesian = (
  cx: number,
  cy: number,
  radius: number,
  angleInDegrees: number,
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number,
) => {
  const clampedOuter = Math.max(0, outerRadius);
  const clampedInner = Math.max(0, Math.min(innerRadius, clampedOuter));
  const angleSpan = Math.max(0, endAngle - startAngle);
  const clampedSpan = Math.min(360, Math.max(0, angleSpan));
  const adjustedEnd = startAngle + clampedSpan;
  const largeArcFlag = clampedSpan <= 180 ? "0" : "1";
  const outerStart = polarToCartesian(cx, cy, clampedOuter, adjustedEnd);
  const outerEnd = polarToCartesian(cx, cy, clampedOuter, startAngle);

  if (clampedOuter <= 0) {
    return `M ${cx.toFixed(2)} ${cy.toFixed(2)}`;
  }

  if (clampedInner <= 0) {
    return [
      `M ${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)}`,
      `A ${clampedOuter.toFixed(2)} ${clampedOuter.toFixed(2)} 0 ${largeArcFlag} 0 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)}`,
      `L ${cx.toFixed(2)} ${cy.toFixed(2)}`,
      "Z",
    ].join(" ");
  }

  const innerStart = polarToCartesian(cx, cy, clampedInner, startAngle);
  const innerEnd = polarToCartesian(cx, cy, clampedInner, adjustedEnd);

  return [
    `M ${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)}`,
    `A ${clampedOuter.toFixed(2)} ${clampedOuter.toFixed(2)} 0 ${largeArcFlag} 0 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)}`,
    `L ${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)}`,
    `A ${clampedInner.toFixed(2)} ${clampedInner.toFixed(2)} 0 ${largeArcFlag} 1 ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)}`,
    "Z",
  ].join(" ");
};

const clampToPositive = (value: number) =>
  Number.isFinite(value) ? Math.max(0, value) : 0;

export interface PieChartAnimatorProps {
  frames: Frame[];
  width?: number;
  height?: number;
  padding?: number;
  duration?: number;
  advanceDelay?: number;
  easing?: string;
  loopMode?: "normal" | "alternate";
  iterations?: number | "infinite";
  stagger?: number;
  labels?: boolean;
  labelFontSize?: number;
  valueLabels?: boolean;
  animateEach?: boolean;
  hold?: { start?: number; end?: number };
  color?: string;
  dataItemStyle?: "striped" | "solid";
  innerRadius?: number;
  startAngle?: number;
  labelOffset?: number;
  className?: string;
  style?: CSSProperties;
  autoPlay?: boolean;
  introPercentOverride?: number;
}

export const PieChartAnimator = forwardRef(function PieChartAnimator(
  props: PieChartAnimatorProps,
  ref: ForwardedRef<AnimatorHandle>,
) {
  const settings = useMemo(
    () => ({
      ...defaultOptions,
      ...props,
      chartType: "pie",
      hold: { ...defaultOptions.hold, ...(props.hold || {}) },
    }),
    [props],
  );

  const {
    frames,
    width,
    height,
    padding,
    duration,
    advanceDelay,
    easing,
    loopMode,
    iterations,
    stagger,
    labels,
    labelFontSize: rawLabelFontSize,
    valueLabels,
    animateEach,
    hold,
    color = "#6c5ce7",
    dataItemStyle = "solid",
    innerRadius: rawInnerRadius,
    startAngle: rawStartAngle,
    labelOffset: rawLabelOffset,
    className,
    style,
    autoPlay = true,
    introPercentOverride,
  } = settings;

  const labelFontSize = Number.isFinite(rawLabelFontSize)
    ? rawLabelFontSize
    : defaultOptions.labelFontSize;
  const labelOffset = Number.isFinite(rawLabelOffset)
    ? rawLabelOffset
    : Math.round(labelFontSize * 0.9);
  const startAngle = Number.isFinite(rawStartAngle) ? rawStartAngle : -90;

  const holdStart = hold?.start ?? defaultHold.start;
  const holdEnd = hold?.end ?? defaultHold.end;

  const animationIdRef = useRef(`ds-pie-${++idCounter}`);
  const baseName = animationIdRef.current;
  const { paused } = useAnimator(ref, autoPlay);
  const labelRefs = useRef<(SVGTextElement | null)[]>([]);
  const valueLabelRefs = useRef<(SVGTextElement | null)[]>([]);
  const firstSliceRef = useRef<SVGPathElement | null>(null);

  if (!Array.isArray(frames) || frames.length === 0) {
    throw new Error("frames must be a non-empty array");
  }

  const { keys } = useMemo(() => normalizeFrames(frames), [frames]);
  const valuesByKey = useMemo(() => {
    return keys.reduce<Record<string, number[]>>((acc, key) => {
      acc[key] = frames.map((frame) => extractValue(frame[key]));
      return acc;
    }, {});
  }, [frames, keys]);

  const itemConfigsByKey = useMemo(() => {
    return keys.reduce<
      Record<string, Array<ReturnType<typeof extractItemConfig>>>
    >((acc, key) => {
      acc[key] = frames.map((frame) => extractItemConfig(frame[key]));
      return acc;
    }, {});
  }, [frames, keys]);

  const valueLabelStylesByKey = useMemo(() => {
    return keys.reduce<
      Record<string, { fill: string; stroke: string; isLight: boolean }>
    >((acc, key) => {
      const itemConfig = itemConfigsByKey[key]?.[0];
      const itemColor = itemConfig?.color || color;
      const isLight = isLightColor(itemColor);
      acc[key] = {
        fill: isLight ? "#111" : "#fff",
        stroke: isLight ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.35)",
        isLight,
      };
      return acc;
    }, {});
  }, [keys, itemConfigsByKey, color]);

  const labelTimings = useMemo(
    () =>
      buildFrameLabelTimings(
        frames,
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      ),
    [frames, holdEnd, holdStart, duration, advanceDelay],
  );

  const frameLabelSpace =
    labelTimings.length > 0
      ? Math.max(FRAME_LABEL_VERTICAL_SPACE, Math.round(labelFontSize * 2))
      : 0;
  const availableHeight = Math.max(0, height - frameLabelSpace);
  const radius = Math.max(0, Math.min(width, availableHeight) / 2 - padding);
  const innerRadius = Math.max(
    0,
    Math.min(radius, Number.isFinite(rawInnerRadius) ? rawInnerRadius : 0),
  );
  const centerX = width / 2;
  const centerY = frameLabelSpace + availableHeight / 2;
  const valueLabelRadius = innerRadius + (radius - innerRadius) * 0.6;

  const introPercent = useMemo(() => {
    if (typeof introPercentOverride === "number") return introPercentOverride;
    if (animateEach && keys.length > 0) {
      return Math.min(40, Math.max(12, keys.length * 24));
    }
    return 0;
  }, [animateEach, introPercentOverride, keys.length]);

  const easingFn = useMemo(() => resolveEasing(easing), [easing]);

  const valueTimelines = useMemo(() => {
    return keys.map((key, index) => {
      let timeline = buildValueTimeline(
        valuesByKey[key],
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      );

      if (introPercent > 0) {
        timeline = applySequentialIntro(
          timeline,
          index,
          keys.length,
          introPercent,
        );
      }
      if (animateEach) {
        timeline = applySequentialTransitions(
          timeline,
          index,
          keys.length,
          introPercent > 0 ? introPercent : 0,
        );
      }

      return timeline;
    });
  }, [
    advanceDelay,
    animateEach,
    duration,
    holdEnd,
    holdStart,
    introPercent,
    keys,
    valuesByKey,
  ]);

  const introValues = useMemo(
    () => keys.map((key) => clampToPositive(valuesByKey[key]?.[0] ?? 0)),
    [keys, valuesByKey],
  );
  const introTotal = useMemo(
    () => introValues.reduce((sum, value) => sum + value, 0),
    [introValues],
  );

  const finalValues = useMemo(
    () =>
      keys.map((key) => {
        const values = valuesByKey[key] ?? [];
        const lastValue = values[values.length - 1];
        return Number.isFinite(lastValue) ? lastValue : 0;
      }),
    [keys, valuesByKey],
  );

  const baseTotalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frames.length,
  );
  const totalDuration =
    introPercent > 0
      ? Math.round(baseTotalDuration / ((100 - introPercent) / 100))
      : baseTotalDuration;
  const effectiveIterations =
    typeof iterations === "number" ? iterations : "infinite";

  const introValuesAtPercent = useMemo(() => {
    if (introPercent <= 0 || keys.length === 0) {
      return (_percent: number) => introValues;
    }

    const segment = introPercent / keys.length;
    if (!Number.isFinite(segment) || segment <= 0) {
      return (_percent: number) => introValues;
    }

    return (percent: number) =>
      introValues.map((value, index) => {
        const start = segment * index;
        const local = (percent - start) / segment;
        const clamped = Math.max(0, Math.min(1, local));
        return value * clamped;
      });
  }, [introPercent, introValues, keys.length]);

  const pathTimeline = useMemo(() => {
    const percentSet = new Set<number>([0, 100]);
    valueTimelines.forEach((timeline) =>
      timeline.forEach((entry) => percentSet.add(entry.percent)),
    );
    if (introPercent > 0) {
      const stepCount = Math.max(12, Math.ceil(introPercent / 2));
      for (let i = 0; i <= stepCount; i += 1) {
        percentSet.add((introPercent * i) / stepCount);
      }
    }
    const percents = Array.from(percentSet).sort((a, b) => a - b);

    return percents.map((percent) => {
      const useIntroTotal = introPercent > 0 && percent <= introPercent;
      const values = useIntroTotal
        ? introValuesAtPercent(percent)
        : valueTimelines.map((timeline) =>
            getValueAtPercent(timeline, percent),
          );
      return {
        percent,
        values,
        totalOverride: useIntroTotal ? introTotal : undefined,
      };
    });
  }, [introPercent, introTotal, introValuesAtPercent, valueTimelines]);

  const buildSlicePaths = useMemo(() => {
    return (values: number[], totalOverride?: number) => {
      const safeValues = values.map(clampToPositive);
      const total = Number.isFinite(totalOverride)
        ? Math.max(0, totalOverride as number)
        : safeValues.reduce((sum, value) => sum + value, 0);
      let currentAngle = startAngle;
      return safeValues.map((value) => {
        const sliceAngle = total > 0 ? (value / total) * 360 : 0;
        const endAngle = currentAngle + sliceAngle;
        const path = describeArc(
          centerX,
          centerY,
          radius,
          innerRadius,
          currentAngle,
          endAngle,
        );
        currentAngle = endAngle;
        return path;
      });
    };
  }, [centerX, centerY, innerRadius, radius, startAngle]);

  const pieKeyframes = useMemo(() => {
    return keys
      .map((_key, index) => {
        const keyframes = pathTimeline
          .map(({ percent, values, totalOverride }) => {
            const paths = buildSlicePaths(values, totalOverride);
            const d =
              paths[index] || `M ${centerX.toFixed(2)} ${centerY.toFixed(2)}`;
            return `${percent.toFixed(2)}% { d: path('${d}'); }`;
          })
          .join("\n");
        return `@keyframes ${baseName}-slice-${index} {\n${keyframes}\n}`;
      })
      .join("\n");
  }, [baseName, buildSlicePaths, centerX, centerY, keys, pathTimeline]);

  const effectiveLabelTimings =
    introPercent > 0
      ? adjustLabelTimingsForIntro(labelTimings, introPercent)
      : labelTimings;
  const labelCSS = useMemo(
    () => buildFrameLabelCSS(baseName, effectiveLabelTimings),
    [baseName, effectiveLabelTimings],
  );
  const maxDelay = Math.max(0, stagger * (keys.length - 1));

  useEffect(() => {
    if (!labels && !valueLabels) return;
    if (paused) {
      return;
    }

    let rafId = 0;
    const step = () => {
      // Get elapsed time from the first slice's CSS animation to stay in sync
      const slice = firstSliceRef.current;
      let elapsed = 0;

      if (slice) {
        const animations = slice.getAnimations();
        const sliceAnimation = animations.find(
          (a) => a instanceof CSSAnimation,
        );
        if (sliceAnimation && sliceAnimation.currentTime != null) {
          elapsed = sliceAnimation.currentTime as number;
        }
      }

      const values = valueTimelines.map((timeline, index) => {
        // Adjust elapsed for stagger offset of this slice relative to first slice
        const sliceElapsed = elapsed + stagger * index;
        const progress = getIterationProgress(
          sliceElapsed,
          stagger * index,
          totalDuration,
          effectiveIterations,
          loopMode,
        );
        return interpolateTimeline(timeline, progress * 100, easingFn);
      });
      const safeValues = values.map(clampToPositive);
      const total = safeValues.reduce((sum, value) => sum + value, 0);

      let currentAngle = startAngle;
      for (let index = 0; index < keys.length; index += 1) {
        const value = safeValues[index] ?? 0;
        const sliceAngle = total > 0 ? (value / total) * 360 : 0;
        const midAngle = currentAngle + sliceAngle / 2;
        const radians = ((midAngle - 90) * Math.PI) / 180;

        if (labels) {
          const label = labelRefs.current[index];
          if (label) {
            const x = centerX + Math.cos(radians) * (radius + labelOffset);
            const y = centerY + Math.sin(radians) * (radius + labelOffset);
            const anchor =
              Math.cos(radians) >= 0.1
                ? "start"
                : Math.cos(radians) <= -0.1
                  ? "end"
                  : "middle";
            label.setAttribute("x", x.toFixed(2));
            label.setAttribute("y", y.toFixed(2));
            if (label.getAttribute("text-anchor") !== anchor) {
              label.setAttribute("text-anchor", anchor);
            }
            const isHidden = total <= 0 || sliceAngle <= 0.1;
            const targetOpacity = isHidden ? "0" : "1";
            if (label.getAttribute("opacity") !== targetOpacity) {
              label.setAttribute("opacity", targetOpacity);
            }
          }
        }

        if (valueLabels) {
          const valueLabel = valueLabelRefs.current[index];
          if (valueLabel) {
            const x = centerX + Math.cos(radians) * valueLabelRadius;
            const y = centerY + Math.sin(radians) * valueLabelRadius;
            const isZero =
              Math.abs(value) < 1e-6 || total <= 0 || sliceAngle <= 0.1;
            if (isZero) {
              if (valueLabel.textContent) {
                valueLabel.textContent = "";
              }
              if (valueLabel.getAttribute("opacity") !== "0") {
                valueLabel.setAttribute("opacity", "0");
              }
            } else {
              const text = formatValueLabel(value, finalValues[index] ?? value);
              if (valueLabel.textContent !== text) {
                valueLabel.textContent = text;
              }
              if (valueLabel.getAttribute("opacity") !== "1") {
                valueLabel.setAttribute("opacity", "1");
              }
            }
            valueLabel.setAttribute("x", x.toFixed(2));
            valueLabel.setAttribute("y", y.toFixed(2));
          }
        }

        currentAngle += sliceAngle;
      }

      if (typeof effectiveIterations === "number") {
        const totalMs =
          totalDuration * Math.max(0, effectiveIterations) + maxDelay;
        if (elapsed >= totalMs) return;
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);

    return () => cancelAnimationFrame(rafId);
  }, [
    centerX,
    centerY,
    easingFn,
    effectiveIterations,
    finalValues,
    keys.length,
    labelOffset,
    labels,
    loopMode,
    maxDelay,
    paused,
    radius,
    stagger,
    startAngle,
    totalDuration,
    valueLabelRadius,
    valueLabels,
    valueTimelines,
  ]);

  const initialPaths = useMemo(() => {
    const initialValues = keys.map((key) => valuesByKey[key]?.[0] ?? 0);
    return buildSlicePaths(
      initialValues,
      introPercent > 0 ? introTotal : undefined,
    );
  }, [buildSlicePaths, introPercent, introTotal, keys, valuesByKey]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Animated pie chart"
      className={className}
      style={{ overflow: "visible", color, ...style }}
    >
      <defs>
        {keys.map((key, index) => {
          const itemConfig = itemConfigsByKey[key]?.[0];
          const itemStyle = itemConfig?.dataItemStyle || dataItemStyle;
          const itemColor = itemConfig?.color || color;

          if (itemStyle === "striped") {
            return (
              <pattern
                key={`${key}-stripe`}
                id={`${baseName}-pie-stripe-${index}`}
                patternUnits="userSpaceOnUse"
                width="32"
                height="32"
                patternTransform="rotate(45)"
              >
                <rect width="16" height="32" fill={itemColor} />
                <rect
                  x="16"
                  width="16"
                  height="32"
                  fill={itemColor}
                  opacity="0.75"
                />
              </pattern>
            );
          }
          return null;
        })}
      </defs>
      <style>{`${pieKeyframes}\n${labelCSS}`}</style>
      <g transform={`translate(0, ${frameLabelSpace})`}>
        {keys.map((key, index) => {
          const itemConfig = itemConfigsByKey[key]?.[0];
          const itemStyle = itemConfig?.dataItemStyle || dataItemStyle;
          const itemColor = itemConfig?.color || color;
          const fillValue =
            itemStyle === "striped"
              ? `url(#${baseName}-pie-stripe-${index})`
              : itemColor;
          const delay = stagger * index;
          const initialValue = clampToPositive(valuesByKey[key]?.[0] ?? 0);

          return (
            <path
              key={key}
              ref={index === 0 ? firstSliceRef : undefined}
              d={initialPaths[index]}
              fill={fillValue}
              opacity={initialValue > 0 ? 1 : 0}
              style={{
                animationName: `${baseName}-slice-${index}`,
                animationDuration: `${totalDuration}ms`,
                animationTimingFunction: easing,
                animationDelay: `${delay}ms`,
                animationIterationCount: effectiveIterations,
                animationDirection: loopMode,
                animationFillMode: "forwards",
                animationPlayState: paused ? "paused" : "running",
              }}
            />
          );
        })}
        {valueLabels ? (
          <g>
            {keys.map((key, index) => {
              const initialValue = clampToPositive(valuesByKey[key]?.[0] ?? 0);
              const totalInitial = keys.reduce(
                (sum, k) => sum + clampToPositive(valuesByKey[k]?.[0] ?? 0),
                0,
              );
              const sliceAngle =
                totalInitial > 0 ? (initialValue / totalInitial) * 360 : 0;
              const angleOffset = keys
                .slice(0, index)
                .reduce(
                  (sum, k) =>
                    sum +
                    (totalInitial > 0
                      ? (clampToPositive(valuesByKey[k]?.[0] ?? 0) /
                          totalInitial) *
                        360
                      : 0),
                  0,
                );
              const midAngle = startAngle + angleOffset + sliceAngle / 2;
              const radians = ((midAngle - 90) * Math.PI) / 180;
              const x = centerX + Math.cos(radians) * valueLabelRadius;
              const y = centerY + Math.sin(radians) * valueLabelRadius;
              const initialHidden = totalInitial <= 0 || sliceAngle <= 0.1;

              const itemConfig = itemConfigsByKey[key]?.[0];
              const showValueLabel = itemConfig?.valueLabels ?? valueLabels;
              const labelStyles = valueLabelStylesByKey[key];

              if (!showValueLabel) return null;

              return (
                <text
                  key={`${key}-value`}
                  ref={(node) => {
                    valueLabelRefs.current[index] = node;
                  }}
                  x={x.toFixed(2)}
                  y={y.toFixed(2)}
                  textAnchor="middle"
                  fontSize={labelFontSize}
                  fontWeight={600}
                  fill={labelStyles.fill}
                  stroke={labelStyles.stroke}
                  strokeWidth={2}
                  paintOrder="stroke"
                  dominantBaseline="middle"
                  opacity={initialHidden ? 0 : 1}
                >
                  {initialHidden
                    ? ""
                    : formatValueLabel(
                        initialValue,
                        finalValues[index] ?? initialValue,
                      )}
                </text>
              );
            })}
          </g>
        ) : null}
        {labels ? (
          <g>
            {keys.map((key, index) => {
              const initialValue = clampToPositive(valuesByKey[key]?.[0] ?? 0);
              const totalInitial = keys.reduce(
                (sum, k) => sum + clampToPositive(valuesByKey[k]?.[0] ?? 0),
                0,
              );
              const sliceAngle =
                totalInitial > 0 ? (initialValue / totalInitial) * 360 : 0;
              const angleOffset = keys
                .slice(0, index)
                .reduce(
                  (sum, k) =>
                    sum +
                    (totalInitial > 0
                      ? (clampToPositive(valuesByKey[k]?.[0] ?? 0) /
                          totalInitial) *
                        360
                      : 0),
                  0,
                );
              const midAngle = startAngle + angleOffset + sliceAngle / 2;
              const radians = ((midAngle - 90) * Math.PI) / 180;
              const x = centerX + Math.cos(radians) * (radius + labelOffset);
              const y = centerY + Math.sin(radians) * (radius + labelOffset);
              const anchor =
                Math.cos(radians) >= 0.1
                  ? "start"
                  : Math.cos(radians) <= -0.1
                    ? "end"
                    : "middle";
              const initialHidden = totalInitial <= 0 || sliceAngle <= 0.1;

              return (
                <text
                  key={`${key}-label`}
                  ref={(node) => {
                    labelRefs.current[index] = node;
                  }}
                  x={x.toFixed(2)}
                  y={y.toFixed(2)}
                  textAnchor={anchor}
                  fontSize={labelFontSize}
                  fill="currentColor"
                  dominantBaseline="middle"
                  opacity={initialHidden ? 0 : 1}
                >
                  {key}
                </text>
              );
            })}
          </g>
        ) : null}
      </g>

      {effectiveLabelTimings.map(({ label }, index) => (
        <text
          key={`frame-label-${index}`}
          x={padding.toFixed(2)}
          y={(padding + labelFontSize).toFixed(2)}
          textAnchor="start"
          fontSize={labelFontSize}
          fontWeight={700}
          fill="currentColor"
          style={{
            animationName: `${baseName}-label-${index}`,
            animationDuration: `${totalDuration}ms`,
            animationTimingFunction: "steps(1, end)",
            animationDelay: "0ms",
            animationIterationCount: effectiveIterations,
            animationDirection: loopMode,
            animationFillMode: "both",
            animationPlayState: paused ? "paused" : "running",
          }}
        >
          {label}
        </text>
      ))}
    </svg>
  );
});

export default PieChartAnimator;
